/**
 * HTTP-клиент веб-штаба: базовый URL, JWT, коды ошибок сервера (§Т2.1, §19.5).
 *
 * Назначение файла: единственная точка fetch к API. Сервер отдаёт error.code,
 * клиент превращает его в ключ каталога errors:<code>.
 */

const API_BASE = import.meta.env["VITE_API_URL"] ?? "/api";

/** Предел ожидания ответа API, мс. Дальше запрос считается потерянным. */
const REQUEST_TIMEOUT_MS = 15_000;

/** Отмена по первому сработавшему сигналу: свой плюс таймаут. */
function anySignal(signals: readonly AbortSignal[]): AbortSignal {
  const merged = new AbortController();
  for (const signal of signals) {
    if (signal.aborted) {
      merged.abort();
      break;
    }
    signal.addEventListener("abort", () => merged.abort(), { once: true });
  }
  return merged.signal;
}

export type ApiErrorBody = {
  readonly error: {
    readonly code: string;
    readonly params?: Record<string, unknown>;
  };
};

export class ApiError extends Error {
  readonly code: string;
  readonly httpStatus: number;
  readonly params: Record<string, unknown>;

  constructor(code: string, httpStatus: number, params: Record<string, unknown> = {}) {
    super(code);
    this.name = "ApiError";
    this.code = code;
    this.httpStatus = httpStatus;
    this.params = params;
  }
}

/** Ключ локализации для кода отказа сервера. */
export function errorI18nKey(code: string): string {
  return `errors:${code}`;
}

function parseJson(text: string): unknown {
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

function errorFromResponse(status: number, body: unknown): ApiError {
  if (body && typeof body === "object" && "error" in body) {
    const err = (body as ApiErrorBody).error;
    if (err?.code) {
      return new ApiError(err.code, status, err.params ?? {});
    }
  }
  if (status === 401) return new ApiError("auth.required", status);
  if (status >= 500) return new ApiError("server.internal", status);
  if (status === 0) return new ApiError("network.offline", 0);
  return new ApiError("network.offline", status);
}

/** Паузы перед повторами запроса при сетевом сбое, мс. */
const RETRY_DELAYS_MS = [400, 1200] as const;

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Повторять можно только запрос без последствий: GET и HEAD. Ритуал, осада и
 * платежи повторяются сервером по Idempotency-Key, а не клиентом втихую.
 */
function isRetriable(method: string | undefined): boolean {
  const verb = (method ?? "GET").toUpperCase();
  return verb === "GET" || verb === "HEAD";
}

/**
 * Запрос к API. Сетевой сбой чтения повторяется: Android поднимает сетевой стек
 * медленнее, чем стартует приложение, и первый запрос после запуска падал,
 * показывая «Улей не отвечает» на живой связи.
 */
export async function apiFetch<T>(
  path: string,
  options: RequestInit & { token?: string | null } = {},
): Promise<T> {
  let lastError: unknown;
  for (const delay of [0, ...RETRY_DELAYS_MS]) {
    if (delay > 0) await sleep(delay);
    try {
      return await requestOnce<T>(path, options);
    } catch (err) {
      const networkFailure = err instanceof ApiError && err.code === "network.offline";
      if (!networkFailure || !isRetriable(options.method)) throw err;
      lastError = err;
    }
  }
  throw lastError;
}

async function requestOnce<T>(
  path: string,
  options: RequestInit & { token?: string | null },
): Promise<T> {
  const { token, ...init } = options;
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (init.body !== undefined && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  /*
   * Запрос обязан закончиться. Android при просыпании приложения держал
   * соединение открытым без ответа, и загрузка застывала на «Улей просыпается…»
   * навсегда: лучше честный отказ через REQUEST_TIMEOUT_MS, чем вечное
   * ожидание. Свой сигнал отмены не ломается: оба сигнала складываются.
   */
  const timeout = new AbortController();
  const timer = setInterval(() => {
    // Приложение в кармане: Android тормозит сеть, и молчание — не потеря.
    if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
    clearInterval(timer);
    timeout.abort();
  }, REQUEST_TIMEOUT_MS);
  const signal = init.signal ? anySignal([init.signal, timeout.signal]) : timeout.signal;

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, { ...init, headers, signal });
  } catch (err) {
    // Отмену вызывающего (сменился bbox, ушёл экран) ошибкой считать нельзя:
    // вызывающий проверяет свой signal и молча пропускает такой ответ.
    if (init.signal?.aborted) throw err;
    throw new ApiError("network.offline", 0);
  } finally {
    clearInterval(timer);
  }

  const text = await response.text();
  const body = parseJson(text);

  if (!response.ok) {
    throw errorFromResponse(response.status, body);
  }

  return body as T;
}
